import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, Plus, MessageSquare, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '@/lib/firebase';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent';
  timestamp: Date;
}

interface Thread {
  id: string;
  lastMessage: string;
  lastMessageTime: Date;
  status: string;
}

interface CustomChatProps {
  visitorToken?: string;
}

export const CustomChat: React.FC<CustomChatProps> = ({ visitorToken }) => {
  const [user] = useAuthState(auth);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [threadId, setThreadId] = useState<string | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [showThreadList, setShowThreadList] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch existing threads
  useEffect(() => {
    const fetchThreads = async () => {
      if (!user?.email) return;

      setIsLoading(true);
      try {
        const response = await deiterateAPI.GET('/hubspot-chat/threads');
        
        if (response?.results) {
          const formattedThreads: Thread[] = response.results.map((thread: any) => ({
            id: thread.id,
            lastMessage: thread.latestMessageText || 'No messages yet',
            lastMessageTime: new Date(thread.latestMessageTimestamp || thread.createdAt),
            status: thread.status
          }));
          
          setThreads(formattedThreads);
          
          // If there are threads, don't auto-select one
          // If no threads, show the thread list (which will show "start new" option)
          setShowThreadList(true);
        }
      } catch (error) {
        console.error('Error fetching threads:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThreads();
  }, [user?.email]);

  // Function to start a new thread
  const startNewThread = async () => {
    if (!user?.email) return;

    setIsLoading(true);
    try {
      const response = await deiterateAPI.POST('/hubspot-chat/thread', {
        body: JSON.stringify({
          email: user.email,
          visitorToken: visitorToken
        })
      });

      if (response?.id) {
        setThreadId(response.id);
        setShowThreadList(false);
        // Add welcome message
        setMessages([{
          id: 'welcome',
          text: "Hi! I'm Harold, your compliance wingman. How can I help you today?",
          sender: 'agent',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error creating new thread:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to select an existing thread
  const selectThread = async (selectedThreadId: string) => {
    setThreadId(selectedThreadId);
    setShowThreadList(false);
    setMessages([]); // Clear messages, they'll be loaded by the polling effect
  };

  // Poll for new messages
  useEffect(() => {
    if (!threadId) return;
    
    // Don't poll if it's a new thread (contact- prefix) with no messages yet
    if (threadId.startsWith('contact-') && messages.length <= 1) {
      return; // Only has welcome message, no need to poll yet
    }

    const pollMessages = async () => {
      try {
        const response = await deiterateAPI.GET(`/hubspot-chat/messages/${threadId}`);
        
        if (response?.results) {
          const formattedMessages: Message[] = response.results.map((msg: any) => ({
            id: msg.id,
            text: msg.text,
            sender: msg.senders?.[0]?.actorId === user?.email ? 'user' : 'agent',
            timestamp: new Date(msg.createdAt)
          }));
          
          setMessages(prev => {
            // Keep the welcome message and merge with fetched messages
            const welcomeMessage = prev.find(m => m.id === 'welcome');
            const existingIds = new Set(prev.map(m => m.id));
            const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));
            
            if (newMessages.length > 0) {
              // Add new messages while keeping existing ones
              return welcomeMessage 
                ? [welcomeMessage, ...prev.filter(m => m.id !== 'welcome'), ...newMessages]
                : [...prev, ...newMessages];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    // Initial fetch
    pollMessages();
    
    // Poll every 3 seconds
    const interval = setInterval(pollMessages, 3000);
    return () => clearInterval(interval);
  }, [threadId, user?.email, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !threadId || !user?.email || isSending) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    // Optimistically add message
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsSending(true);

    try {
      await deiterateAPI.POST('/hubspot-chat/message', {
        body: JSON.stringify({
          threadId: threadId,
          message: inputMessage,
          senderEmail: user.email
        })
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== newMessage.id));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading && !showThreadList) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show thread list
  if (showThreadList) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-white/10 bg-white/5 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-foreground">Your Conversations</h3>
          <p className="text-sm text-muted-foreground">Select a conversation or start a new one</p>
        </div>
        
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-2">
            {/* New Chat Button */}
            <Button
              onClick={startNewThread}
              className="w-full justify-start bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Start New Conversation
            </Button>

            {/* Existing Threads */}
            {threads.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2">
                  Recent Conversations
                </p>
                {threads.map((thread) => (
                  <button
                    key={thread.id}
                    onClick={() => selectThread(thread.id)}
                    className="w-full text-left p-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{thread.lastMessage}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {thread.lastMessageTime.toLocaleDateString()} at {thread.lastMessageTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {threads.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
                <p className="text-xs text-muted-foreground mt-1">Start a new conversation to get help</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Show chat interface
  return (
    <div className="flex flex-col h-full">
      {/* Header with back button */}
      <div className="p-4 border-b border-white/10 bg-white/5 backdrop-blur-sm flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowThreadList(true)}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-sm font-semibold text-foreground">Chat with Harold</h3>
          <p className="text-xs text-muted-foreground">Your compliance wingman</p>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.sender === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/10 backdrop-blur-sm border border-white/20 text-foreground'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                <span className="text-xs opacity-70 mt-1 block">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-white/10 p-4 bg-white/5 backdrop-blur-sm">
        <div className="flex gap-2">
          <Input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isSending}
            className="flex-1 bg-white/10 backdrop-blur-sm border-white/20"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isSending}
            className="bg-primary hover:bg-primary/90"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
